import { NavLink } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Crosshair, SlidersHorizontal, BookOpen, History, Wrench, ScanLine, HelpCircle, Smartphone, Car, Focus, Disc3, Scale, FileText } from "lucide-react";
import { useVehicle } from "../context/VehicleContext";
import { LanguageSwitcher } from "./LanguageSwitcher";

const tabs = [
  { to: "/app", key: "quick", icon: Crosshair, testId: "nav-quick", end: true },
  { to: "/gauge", key: "tilt", icon: Smartphone, testId: "nav-gauge" },
  { to: "/ar", key: "ar", label: "AR Overlay", icon: Focus, testId: "nav-ar" },
  { to: "/scan", key: "scan", icon: ScanLine, testId: "nav-scan" },
  { to: "/tire", key: "tire", label: "Tire Wear", icon: Disc3, testId: "nav-tire" },
  { to: "/balance", key: "balance", label: "Balance", icon: Scale, testId: "nav-balance" },
  { to: "/calculator", key: "calc", icon: SlidersHorizontal, testId: "nav-calculator" },
  { to: "/report", key: "report", label: "Report", icon: FileText, testId: "nav-report" },
  { to: "/specs", key: "spec", icon: BookOpen, testId: "nav-specs" },
  { to: "/log", key: "log", icon: History, testId: "nav-log" },
  { to: "/guide", key: "guide", icon: Wrench, testId: "nav-guide" },
  { to: "/help", key: "howto", icon: HelpCircle, testId: "nav-help" },
];

export const Nav = () => {
  const { vehicleLabel } = useVehicle();
  const { t } = useTranslation();
  return (
    <header
      data-testid="app-header"
      className="sticky top-0 z-50 bg-ink/80 backdrop-blur-xl border-b border-steel"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          <div className="flex items-center gap-3">
            <NavLink to="/" className="shrink-0 pulse-ring" data-testid="nav-home-brand">
              <img src="/thortek-logo.png" alt="Thortek Performance" className="h-8 w-auto" />
            </NavLink>
            <div className="leading-none">
              <NavLink to="/garage" data-testid="nav-vehicle" title={vehicleLabel}
                className="group flex items-center gap-1 font-mono text-[10px] sm:text-xs text-slate-500 hover:text-cyan tracking-widest transition-colors">
                <Car size={11} className="text-cyan" />
                <span className="truncate max-w-[180px]">{vehicleLabel || t("nav.selectVehicle")}</span>
                <span className="text-cyan opacity-0 group-hover:opacity-100 transition-opacity">· change</span>
              </NavLink>
            </div>
          </div>
          <nav className="hidden md:flex items-center gap-1">
            {tabs.map((t2) => (
              <NavLink
                key={t2.to}
                to={t2.to}
                end={t2.end}
                data-testid={t2.testId}
                className={({ isActive }) =>
                  `flex items-center gap-2 px-4 py-2 rounded-lg font-mono text-xs uppercase tracking-widest transition-all duration-300 ${
                    isActive
                      ? "bg-cyan text-ink font-bold shadow-lg shadow-cyan/25"
                      : "text-slate-400 hover:text-white hover:bg-elevated"
                  }`
                }
              >
                <t2.icon size={15} />
                {t(`nav.${t2.key}`, t2.label)}
              </NavLink>
            ))}
            <LanguageSwitcher />
          </nav>
          <div className="md:hidden"><LanguageSwitcher testId="lang-switcher-mobile" /></div>
        </div>
      </div>
      {/* Mobile tab strip */}
      <nav className="md:hidden flex border-t border-steel overflow-x-auto px-2">
        {tabs.map((t2) => (
          <NavLink
            key={t2.to}
            to={t2.to}
            end={t2.end}
            data-testid={`${t2.testId}-mobile`}
            className={({ isActive }) =>
              `flex-1 min-w-[64px] flex flex-col items-center gap-1 py-2 font-mono text-[10px] uppercase tracking-wider transition-colors ${
                isActive ? "text-cyan" : "text-slate-500"
              }`
            }
          >
            <t2.icon size={17} />
            {t(`nav.${t2.key}`, t2.label)}
          </NavLink>
        ))}
      </nav>
    </header>
  );
};
